import { Router } from "@vaadin/router";

// Import Web Awesome components and set base path
import "@awesome.me/webawesome/dist/styles/themes/default.css";
import { setBasePath } from "@awesome.me/webawesome/dist/utilities/base-path.js";
setBasePath("https://cdn.jsdelivr.net/npm/@awesome.me/webawesome/dist/");

// Import Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css";

// Import our styles
import "./styles/app.css";

// Import components
import "./components/app-controller";
import "./components/app-shell";
import "./pages/login-page";
import "./pages/home-page";
import "./pages/dashboard-page";
import "./components/header-date-stepper";
import "./pages/add-entry-page";
import "./pages/add-picker-page";
import "./pages/add-long-page";
import "./pages/add-exercise-page";
import "./pages/add-ketone-page";
import "./pages/add-sick-page";
import "./pages/add-quick-page";
import "./pages/add-meal-page";
import "./pages/history-page";
import "./pages/settings-page";
import "./pages/sample-page";
import "./pages/upload-page";
import "./pages/share-page";
import "./pages/accept-invite-page";
import "./pages/monitoring-page";
import "./pages/admin-page";
import "./pages/help-page";
import "./pages/diagnostic-page";
import "./pages/support-page";
import "./pages/support-request-new-page";
import "./pages/support-request-detail-page";
import "./pages/support-dashboard-page";
import "./pages/support-request-clinician-page";
import "./pages/quick-gl-page";
import "./pages/debug-logs-page";
import "./pages/debug-log-detail-page";

// Auth state change handler for router setup
import { authService } from "./services/auth";

let routerInitialized = false;

function initializeRouter() {
  if (routerInitialized) return;

  const outlet = document.getElementById("outlet");
  if (outlet) {
    const router = new Router(outlet);
    router.setRoutes([
      { path: "/", component: "home-page" },
      { path: "/dashboard", component: "dashboard-page" },
      { path: "/login", redirect: "/" }, // Redirect to home - app-controller shows login when not authenticated
      { path: "/quick-gl", component: "quick-gl-page" },
      { path: "/add", component: "add-picker-page" },
      { path: "/add/meal", component: "add-meal-page" },
      { path: "/add/quick", component: "add-quick-page" },
      { path: "/add/long", component: "add-long-page" },
      { path: "/add/exercise", component: "add-exercise-page" },
      { path: "/add/ketone", component: "add-ketone-page" },
      { path: "/add/sick", component: "add-sick-page" },
      { path: "/edit/:id", component: "add-entry-page" },
      { path: "/history", component: "history-page" },
      { path: "/sample", component: "sample-page" },
      { path: "/settings", component: "settings-page" },
      { path: "/upload", component: "upload-page" },
      { path: "/share", component: "share-page" },
      { path: "/invite/:code", component: "accept-invite-page" },
      { path: "/monitoring", component: "monitoring-page" },
      { path: "/admin", component: "admin-page" },
      { path: "/admin/debug-logs", component: "debug-logs-page" },
      { path: "/admin/debug-logs/:id", component: "debug-log-detail-page" },
      { path: "/help", component: "help-page" },
      { path: "/diagnostic", component: "diagnostic-page" },
      { path: "/support", component: "support-page" },
      { path: "/support/new", component: "support-request-new-page" },
      {
        path: "/support/request/:id",
        component: "support-request-detail-page",
      },
      { path: "/support-dashboard", component: "support-dashboard-page" },
      {
        path: "/support-dashboard/request/:id",
        component: "support-request-clinician-page",
      },
    ]);
    routerInitialized = true;
  }
}

// Initialize router when user authenticates
authService.subscribe((state) => {
  if (state.isAuthenticated && !state.isLoading) {
    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => initializeRouter());
  }
});

// Register service worker with prompt-based update strategy.
//
// Flow:
//  1. We poll registration.update() every 60s and on tab visibility change.
//  2. When a new SW is detected, the browser puts it in "waiting" state.
//  3. We listen for `updatefound` → new worker `statechange` to "installed",
//     and only if there is already a controller (not the very first install)
//     we show an in-app banner asking the user to reload.
//  4. The user clicks Reload → we postMessage SKIP_WAITING → the SW activates
//     → `controllerchange` fires → we reload the page.
//
// The earlier code never sent SKIP_WAITING, so updates were stuck waiting
// indefinitely on phones where the PWA window is never closed.
import { showUpdatePrompt } from "./services/update-prompt";
import { toast } from "./services/notifications";

// One-time notice if the home-screen icon has changed since this device last
// saw the app. PWA icons are cached by the OS at install time and cannot be
// auto-refreshed — the only fix is for the user to remove and re-add the
// app to their home screen, so we tell them.
try {
  const STORAGE_KEY = "dafne_icon_version";
  const seen = Number(localStorage.getItem(STORAGE_KEY) || "0");
  if (seen > 0 && seen !== __ICON_VERSION__) {
    setTimeout(() => {
      toast.info(
        "App icon updated. Remove and re-add this app to your home screen to see the new icon.",
        { duration: 8000 },
      );
    }, 2000);
  }
  localStorage.setItem(STORAGE_KEY, String(__ICON_VERSION__));
} catch {
  // localStorage may be unavailable (private mode) — non-critical
}

if ("serviceWorker" in navigator) {
  let reloadAfterControllerChange = true;

  function promptForWaitingWorker(worker: ServiceWorker) {
    showUpdatePrompt(() => {
      worker.postMessage({ type: "SKIP_WAITING" });
    });
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");

      // If there's already a waiting worker at registration time
      // (we missed the updatefound event because the page just loaded),
      // prompt immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        promptForWaitingWorker(registration.waiting);
      }

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            // A real update — first install has no controller yet.
            promptForWaitingWorker(newWorker);
          }
        });
      });

      // Check for updates every 60 seconds and on tab visibility change.
      setInterval(() => {
        registration.update().catch(() => {});
      }, 60 * 1000);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          registration.update().catch(() => {});
        }
      });
    } catch {
      // Service worker registration failed - app still works
    }
  });

  // When the new SW activates (after SKIP_WAITING), reload the page so the
  // user is on the new version. Guard against double-reloads.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadAfterControllerChange) return;
    reloadAfterControllerChange = false;
    window.location.reload();
  });
}
